import { api, publicApiBase } from "../api";
import { h } from "../ui/dom";
import { safeMessage } from "./shared/safe";

type Tool = {
  name: string;
  title: string;
  mutating: boolean;
  external: boolean;
  defaultEnabled: boolean;
};
type Grant = {
  id: string;
  name: string;
  clientAccess?: "selected" | "all";
  clientIds: string[];
  newClientSlugs: string[];
  tools: string[];
  publications: unknown[];
  articleActions?: unknown[];
  generation?: unknown[];
  campaigns?: unknown[];
  expiresAt: number;
  revokedAt: number | null;
};
export async function aiAccessView(root: HTMLElement) {
  const status = h("p", { role: "status" }, "Loading AI access…");
  root.replaceChildren(
    h("div", { class: "topbar" }, h("h1", null, "AI access")),
    h(
      "p",
      null,
      "Give each AI connection separate, revocable access. Read-only access is selected first. Changes, approvals and publication require permission from you.",
    ),
    h(
      "p",
      null,
      "Remote MCP address: ",
      h("code", null, publicApiBase().replace(/\/$/, "") + "/mcp"),
    ),
    status,
  );
  try {
    const [access, clientData] = await Promise.all([
      api<{ grants: Grant[]; tools: Tool[]; limitations: string[] }>(
        "/v1/ai-access",
      ),
      api<{ clients: { id: string; business_name: string }[] }>(
        "/v1/clients?limit=200",
      ),
    ]);
    const name = h("input", {
      id: "ai-name",
      required: true,
      maxlength: 100,
      placeholder: "e.g. Claude — demo client",
    });
    const expiry = h("input", {
      id: "ai-expiry",
      type: "datetime-local",
      required: true,
    });
    const date = new Date(Date.now() + 7 * 86400000);
    expiry.value = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    const allClients = h("input", {id:"ai-all-clients",type:"checkbox"});
    const allClientsLabel = h("label", {class:"check-row"}, allClients, " Manage all current and future clients");
    const allClientsNote = h("p", null, "Includes creating clients, maintaining their data and viewing every connected account. Publication approvals, paid generation allowances and expiry remain separate. Provider keys stay private.");
    const newSlugs = h("textarea", {
      id: "ai-new-clients",
      rows: 2,
      placeholder: "e.g. demo-plumber, demo-dentist",
    });
    const selectedClients = new Map<string, HTMLInputElement>();
    const clients = h(
      "fieldset",
      null,
      h("legend", null, "Clients this AI may access"),
    );
    for (const c of clientData.clients) {
      const box = h("input", { type: "checkbox", value: c.id });
      selectedClients.set(c.id, box);
      clients.append(
        h("label", { class: "check-row" }, box, " ", c.business_name),
      );
    }
    allClients.onchange = () => {
      clients.hidden = allClients.checked;
      newSlugs.disabled = allClients.checked;
    };
    const operations = h(
      "fieldset",
      null,
      h("legend", null, "Allowed operations"),
    );
    const selectedTools = new Map<string, HTMLInputElement>();
    // Fan-out article publication and standalone republish remain unavailable; retries/changes need exact scopes below.
    const unavailable = new Set([
      "cp_publish_content",
      "cp_republish_content",
    ]);
    for (const t of access.tools.filter((t) => !unavailable.has(t.name))) {
      const box = h("input", {
        type: "checkbox",
        checked: t.defaultEnabled,
        value: t.name,
      });
      selectedTools.set(t.name, box);
      operations.append(
        h(
          "label",
          { class: "check-row" },
          box,
          " ",
          t.title,
          t.external ? " — contacts a provider" : "",
        ),
      );
    }
    const generationControls = new Map<string, { cents: HTMLInputElement; calls: HTMLInputElement }>();
    const generationPanel = h("fieldset", null, h("legend", null, "Optional paid generation allowance"), h("p", null, "Zero allows no paid calls. These total limits apply for this grant’s lifetime, in addition to the monthly client and campaign limits. Select generation operations separately."));
    for (const client of clientData.clients) {
      const cents = h("input", { type: "number", min: 0, max: 100000, value: 0, "aria-label": client.business_name + " AI allowance in cents" });
      const calls = h("input", { type: "number", min: 0, max: 1000, value: 0, "aria-label": client.business_name + " AI generation call limit" });
      generationControls.set(client.id, { cents, calls });
      generationPanel.append(h("div", null, h("strong", null, client.business_name), h("label", null, " Total cents ", cents), h("label", null, " Maximum calls ", calls)));
    }
    const campaignPermissions: {campaignId: string; revision: number; approvalHash: string}[] = [];
    const campaignPanel = h("fieldset", null, h("legend", null, "Optional approved campaign authority"), h("p", null, "Approve the exact plan in Campaigns first. AI can run only its approved sources, accounts, targets, placements and publication ceiling. Select the run operation separately."));
    const loadCampaigns = h("button", {type: "button"}, "Choose approved campaigns");
    const campaignChoices = h("div");
    loadCampaigns.onclick = async () => {
      loadCampaigns.disabled = true;
      try {
        const data = await api<{campaigns: {id:string;name:string;state:string;revision:number;approval_hash:string|null}[]}>("/v1/campaigns");
        campaignChoices.replaceChildren();
        for (const plan of data.campaigns.filter(c=>c.state==='active' && c.approval_hash)) {
          const box = h("input", {type:"checkbox",checked:campaignPermissions.some(p=>p.campaignId===plan.id)});
          box.onchange = () => {
            const index = campaignPermissions.findIndex(p=>p.campaignId===plan.id);
            if(index>=0) campaignPermissions.splice(index,1);
            if(box.checked) {campaignPermissions.push({campaignId:plan.id,revision:plan.revision,approvalHash:plan.approval_hash!}); const run=selectedTools.get('cp_run_campaign');if(run)run.checked=true;}
          };
          campaignChoices.append(h("label", null, box, " ", plan.name, " · approved revision ", plan.revision));
        }
        if(!campaignChoices.childNodes.length) campaignChoices.append(h('p',null,'No active approved campaigns.'));
      } catch(error) {status.textContent=safeMessage(error instanceof Error?error.message:error);}
      finally {loadCampaigns.disabled=false;}
    };
    campaignPanel.append(loadCampaigns,campaignChoices);
    const permissions: Record<string, unknown>[] = [];
    const articleActions: Record<string, unknown>[] = [];
    const pubClient = h(
      "select",
      { "aria-label": "Publication client" },
      h("option", { value: "" }, "Select a client"),
    );
    for (const c of clientData.clients)
      pubClient.append(h("option", { value: c.id }, c.business_name));
    const pubSite = h(
      "select",
      { "aria-label": "Approved site" },
      h("option", { value: "" }, "Select a recorded release"),
    );
    const pubProvider = h(
      "select",
      { "aria-label": "Publication provider" },
      h("option", { value: "" }, "Select a supported provider"),
    );
    let sites: {
      siteId: string;
      kitId: string;
      currentReleaseId: string | null;
    }[] = [];
    let providers: {
      platform: string;
      connectionId: string;
      connectionRevision: number;
    }[] = [];
    const pubList = h("ul");
    pubClient.onchange = async () => {
      try {
        pubSite.replaceChildren(
          h("option", { value: "" }, "Select a recorded release"),
        );
        pubProvider.replaceChildren(
          h("option", { value: "" }, "Select a supported provider"),
        );
        if (!pubClient.value) return;
        const [s, p] = await Promise.all([
          api<{ sites: typeof sites }>(
            `/v1/clients/${encodeURIComponent(pubClient.value)}/kit-sites`,
          ),
          api<{ providers: Record<string, unknown>[] }>(
            `/v1/clients/${encodeURIComponent(pubClient.value)}/kit-publication-options`,
          ),
        ]);
        sites = s.sites;
        for (const site of sites.filter((s) => s.currentReleaseId))
          pubSite.append(
            h(
              "option",
              { value: site.siteId },
              site.kitId + " — " + site.siteId,
            ),
          );
        providers = p.providers
          .filter((p: any) => p.supported && p.connected && p.connection)
          .map((p: any) => ({
            platform: p.platform,
            connectionId: p.connection.id,
            connectionRevision: p.connection.revision,
          }));
        for (const p of providers)
          pubProvider.append(h("option", { value: p.platform }, p.platform));
      } catch (error) {
        status.textContent = safeMessage(
          error instanceof Error ? error.message : error,
        );
      }
    };
    const addPermission = h(
      "button",
      { type: "button" },
      "Allow this exact publication",
    );
    addPermission.onclick = async () => {
      try {
        const clientId = pubClient.value,
          site = sites.find((s) => s.siteId === pubSite.value),
          provider = providers.find((p) => p.platform === pubProvider.value);
        if ((!allClients.checked && !selectedClients.get(clientId)?.checked) || !site || !provider)
          throw Error(
            "Select an allowed client, recorded site release and available provider.",
          );
        const r = await api<{
          release: {
            releaseId: string;
            releaseHash: string;
            buildId: string;
            destination: { root: string };
          };
        }>(
          `/v1/clients/${encodeURIComponent(clientId)}/kit-sites/${encodeURIComponent(site.siteId)}/releases/current`,
        );
        const b = await api<{
          builds: { recordId: string; buildId: string }[];
        }>(
          `/v1/clients/${encodeURIComponent(clientId)}/kit-sites/${encodeURIComponent(site.siteId)}/builds`,
        );
        const build = b.builds.find((b) => b.buildId === r.release.buildId);
        if (!build)
          throw Error(
            "The released build was not found. Refresh and review the site.",
          );
        const permission = {
          clientId,
          siteId: site.siteId,
          buildRecordId: build.recordId,
          releaseId: r.release.releaseId,
          expectedReleaseHash: r.release.releaseHash,
          ...provider,
        };
        if (
          permissions.some(
            (p) => JSON.stringify(p) === JSON.stringify(permission),
          )
        )
          throw Error("This publication is already in the access request.");
        permissions.push(permission);
        selectedTools.get("cp_publish_kit_build")!.checked = true;
    pubList.append(
          h(
            "li",
            null,
            site.kitId +
              " → " +
              provider.platform +
              " at " +
              r.release.destination.root,
            h(
              "button",
              {
                type: "button",
                on: {
                  click: (e) => {
                    permissions.splice(permissions.indexOf(permission), 1);
                    (e.target as HTMLElement).parentElement!.remove();
                  },
                },
              },
              "Remove permission",
            ),
          ),
        );
        status.textContent =
          "The selected immutable build and account will be authorized. This does not publish it.";
      } catch (error) {
        status.textContent = safeMessage(
          error instanceof Error ? error.message : error,
        );
      }
    };
    const articleActionsList = h("ul");
    const articleActionClient = h("select", { "aria-label": "Article action client" }, h("option", { value: "" }, "Select a client"));
    for (const c of clientData.clients) articleActionClient.append(h("option", { value: c.id }, c.business_name));
    const articleChoices = h("div");
    const loadArticleActions = h("button", { type: "button" }, "Choose delivered articles");
    loadArticleActions.onclick = async () => {
      try {
        if (!articleActionClient.value) throw Error("Choose a client first.");
        const clientId = articleActionClient.value;
        const [delivered, approved, published] = await Promise.all([
          api<{publications: {receiptId:string;platform:string;url:string;masterId:string;state:string;capabilities:{update:boolean;withdraw:boolean}}[]}>(`/v1/content/publications?clientId=${encodeURIComponent(clientId)}`),
          api<{content: {id:string;title:string;status:string}[]}>(`/v1/clients/${encodeURIComponent(clientId)}/content?status=approved&limit=200`),
          api<{content: {id:string;title:string;status:string}[]}>(`/v1/clients/${encodeURIComponent(clientId)}/content?status=published&limit=200`),
        ]);
        const masters = [...approved.content, ...published.content];
        articleChoices.replaceChildren();
        for (const receipt of delivered.publications.filter(p => p.state === "delivered" && (p.capabilities.update || p.capabilities.withdraw))) {
          const row = h("div", { class: "card" });
          const op = h("select", { "aria-label": `Change operation for ${receipt.platform} receipt ${receipt.receiptId}` }, h("option", { value: "" }, "Select an exact action"));
          if (receipt.capabilities.update) op.append(h("option", { value: "update" }, "Update with approved article"));
          if (receipt.capabilities.withdraw) op.append(h("option", { value: "withdraw" }, "Withdraw this post"));
          const replacement = h("select", { "aria-label": `Approved replacement for ${receipt.receiptId}`, hidden: true }, h("option", { value: "" }, "Select an approved replacement"));
          for (const master of masters.filter(m => m.id !== receipt.masterId)) replacement.append(h("option", { value: master.id }, `${master.title} · ${master.status}`));
          op.onchange = () => { replacement.hidden = op.value !== "update"; };
          const add = h("button", { type: "button" }, "Authorize this exact action");
          add.onclick = () => {
            try {
              if (!op.value) throw Error("Select update or withdraw.");
              if (op.value === "update" && !replacement.value) throw Error("Select the exact approved replacement article.");
              const permission = { clientId, receiptId: receipt.receiptId, operation: op.value, ...(op.value === "update" ? { masterId: replacement.value } : {}) };
              if (articleActions.some(a => JSON.stringify(a) === JSON.stringify(permission))) throw Error("That exact article action is already added.");
              articleActions.push(permission);
              selectedTools.get("cp_change_publication")!.checked = true;
              articleActionsList.append(h("li", null, `${receipt.platform} · ${receipt.url} · ${op.value}${permission.masterId ? ` → ${masters.find(m => m.id === permission.masterId)?.title}` : ""}`, h("button", { type:"button", on:{click:(e)=>{articleActions.splice(articleActions.indexOf(permission),1);(e.target as HTMLElement).parentElement!.remove();}} }, "Remove permission")));
              status.textContent = "This exact receipt and operation will be authorized. The provider change still checks current receipt, account revision and provider capability at send time.";
            } catch (error) { status.textContent = safeMessage(error instanceof Error ? error.message : error); }
          };
          row.append(h("p", null, `${receipt.platform} · ${receipt.url} · receipt ${receipt.receiptId}`), op, replacement, add);
          articleChoices.append(row);
        }
        if (!articleChoices.childNodes.length) articleChoices.append(h("p", null, "No current delivered receipt with supported update or withdraw actions."));
      } catch (error) { status.textContent = safeMessage(error instanceof Error ? error.message : error); }
    };
    const articleActionPanel = h("fieldset", null,
      h("legend", null, "Optional exact article update or withdrawal permissions"),
      h("p", null, "Choose one delivered provider receipt, one supported operation and, for updates, one approved replacement article. Standalone republish remains unavailable because it fans out across destinations."),
      articleActionClient, loadArticleActions, articleChoices, articleActionsList,
    );
    const create = h(
      "button",
      { type: "submit", class: "primary" },
      "Create AI access",
    );
    const form = h(
      "form",
      { class: "card" },
      h("label", { for: "ai-name" }, "Connection name"),
      name,
      h("label", { for: "ai-expiry" }, "Expires"),
      expiry,
      allClientsLabel,
      allClientsNote,
      clients,
      h(
        "label",
        { for: "ai-new-clients" },
        "Optional new clients this AI may create (specific slugs, separated by commas or lines)",
      ),
      newSlugs,
      h(
        "details",
        null,
        h("summary", null, "Review allowed operations"),
        operations,
      ),
      h(
        "fieldset",
        null,
        h("legend", null, "Optional exact publication permissions"),
        h(
          "p",
          null,
          "Review the preview and record its release in the client’s Site screen first. A changed build or account requires a new permission.",
        ),
        pubClient,
        pubSite,
        pubProvider,
        addPermission,
        pubList,
      ),
      generationPanel,
      campaignPanel,
      articleActionPanel,
      create,
    );
    form.onsubmit = async (e) => {
      e.preventDefault();
      create.disabled = true;
      try {
        await api("/v1/ai-access", {
          method: "POST",
          body: JSON.stringify({
            name: name.value,
            clientAccess: allClients.checked ? "all" : "selected",
            expiresAt: Math.floor(new Date(expiry.value).getTime() / 1000),
            newClientSlugs: newSlugs.value.split(/[\s,]+/).filter(Boolean),
            clientIds: [...new Set([...selectedClients]
              .filter(([, b]) => b.checked)
              .map(([id]) => id).concat(articleActions.map(a => String(a.clientId))))],
            tools: [...selectedTools]
              .filter(([, b]) => b.checked)
              .map(([id]) => id),
            publications: permissions,
            articleActions,
            generation: [...generationControls].filter(([,v])=>Number(v.cents.value)>0 || Number(v.calls.value)>0).map(([clientId,v])=>({clientId,maxCents:Number(v.cents.value),maxCalls:Number(v.calls.value)})),
            campaigns: campaignPermissions,
          }),
        });
        await aiAccessView(root);
      } catch (error) {
        status.textContent = safeMessage(
          error instanceof Error ? error.message : error,
        );
        create.disabled = false;
      }
    };
    root.append(form, h("h2", null, "Existing connections"));
    for (const g of access.grants) {
      const active = !g.revokedAt && g.expiresAt > Date.now() / 1000;
      const revoke = h(
        "button",
        { type: "button", disabled: !active },
        "Revoke access",
      );
      revoke.onclick = async () => {
        revoke.disabled = true;
        try {
          await api(`/v1/ai-access/${encodeURIComponent(g.id)}/revoke`, {
            method: "POST",
          });
          await aiAccessView(root);
        } catch (error) {
          status.textContent = safeMessage(
            error instanceof Error ? error.message : error,
          );
          revoke.disabled = false;
        }
      };
      const broaden = h("button", {type:"button",disabled:!active || g.clientAccess === "all"}, g.clientAccess === "all" ? "All clients enabled" : "Enable all-client management");
      broaden.onclick = async () => {
        broaden.disabled = true;
        try {
          await api(`/v1/ai-access/${encodeURIComponent(g.id)}/all-clients`,{method:"POST",body:JSON.stringify({confirmAllClients:true})});
          await aiAccessView(root);
        } catch(error) {
          status.textContent = safeMessage(error instanceof Error ? error.message : error);
          broaden.disabled = false;
        }
      };
      root.append(
        h(
          "section",
          { class: "card" },
          h("h3", null, g.name),
          h(
            "p",
            null,
            `${active ? "Active" : "Expired or revoked"} · ${g.clientAccess === "all" ? "All current and future clients" : g.clientIds.length + " clients"} · ${g.tools.length} operations · ${g.publications.length} exact site publications · ${g.articleActions?.length ?? 0} exact article actions · ${g.campaigns?.length ?? 0} approved campaigns · ${g.generation?.length ?? 0} spending allowances · expires ${new Date(g.expiresAt * 1000).toLocaleString()}`,
          ),
          h(
            "p",
            null,
            "Use this access name when the AI client opens CloudPush’s authorization page.",
          ),
          broaden,
          revoke,
        ),
      );
    }
    status.textContent = access.grants.length
      ? "Access loaded. Revocation stops new requests and queued publications. An already-sent provider request cannot be undone."
      : "No AI access granted yet.";
  } catch (error) {
    status.textContent = safeMessage(
      error instanceof Error ? error.message : error,
    );
  }
}
